import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw, HelpCircle, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import Layout from '../components/layout/Layout';
import { comprendreContent } from '../data/mock';

const ComprendrePage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 medical-pattern opacity-50" />
        <div className="decorative-circle w-96 h-96 -top-48 -right-48 opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full text-teal-700 text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              <span>Information patient</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-light text-gray-900">
              {comprendreContent.intro.title}
            </h1>
            <p className="mt-6 text-xl text-gray-600 leading-relaxed">
              {comprendreContent.intro.text}
            </p>
          </div>
        </div>
      </section>

      {/* When Necessary vs When Not */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* When Necessary */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-teal-100/50 overflow-hidden hover-lift">
              <div className="h-1 bg-gradient-to-r from-teal-500 to-teal-600" />
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-teal-800">
                  <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  {comprendreContent.whenNecessary.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {comprendreContent.whenNecessary.points.map((point, index) => (
                    <li key={index} className="flex items-start gap-3 text-teal-900">
                      <span className="w-6 h-6 rounded-full bg-teal-200 text-teal-800 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* When Not Necessary */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-gray-50 to-gray-100/50 overflow-hidden hover-lift">
              <div className="h-1 bg-gradient-to-r from-gray-400 to-gray-500" />
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-gray-800">
                  <div className="w-10 h-10 rounded-xl bg-gray-500 flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-white" />
                  </div>
                  {comprendreContent.whenNot.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {comprendreContent.whenNot.points.map((point, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto" />

      {/* Misconceptions */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-medium text-gray-900">
                {comprendreContent.misconceptions.title}
              </h2>
              <p className="text-sm text-gray-500">Ce que les patients pensent souvent à tort</p>
            </div>
          </div>
          
          <div className="space-y-6">
            {comprendreContent.misconceptions.items.map((item, index) => (
              <Card 
                key={index} 
                className="border border-gray-100 shadow-sm hover:shadow-md hover:border-amber-200 transition-all duration-300 overflow-hidden"
              >
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Myth */}
                    <div className="md:w-1/2 p-6 bg-amber-50 border-b md:border-b-0 md:border-r border-amber-100">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <HelpCircle className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-amber-600 uppercase tracking-wider mb-1">Idée reçue</p>
                          <p className="text-gray-800 font-medium">
                            « {item.myth} »
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Reality */}
                    <div className="md:w-1/2 p-6">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-4 h-4 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-1">Réalité</p>
                          <p className="text-gray-600">
                            {item.reality}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Follow Up */}
      <section className="py-16 bg-gray-50 medical-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 text-teal-700" />
                  </div>
                  <h2 className="text-2xl font-medium text-gray-900">
                    {comprendreContent.followUp.title}
                  </h2>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {comprendreContent.followUp.text}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Key Message */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-700 to-teal-800 p-12 lg:p-16">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '32px 32px'
              }} />
            </div>
            
            <div className="relative max-w-2xl mx-auto text-center">
              <p className="text-2xl lg:text-3xl text-white leading-relaxed font-light">
                "La meilleure chirurgie est parfois celle que l'on ne fait pas.
                Mon rôle est de vous guider vers la décision la plus adaptée à votre situation."
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ComprendrePage;
